import React, { useState, useEffect } from "react";
import { Languages, RotateCcw, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * TranslationPanel — the ONE place translation is invoked from the UI.
 * Spec: /app/backend/specs/translation_service_v1.md
 *
 * Wraps any patient-facing text with:
 *   • EN · ES · PT pill toggle (persists selection to localStorage.patient_output_lang)
 *   • Inline swap to translated content
 *   • "Show original English" button when non-English
 *   • Preserved medical-term chips (from response.warnings)
 *
 * Props:
 *   content: string — the English source text (required)
 *   docType: 'discharge_summary' | 'patient_instructions' | 'patient_education'
 *   testid: string — data-testid root (default 'translation-panel')
 *
 * Clinician-facing surfaces MUST NOT use this component.  It is exclusive to
 * patient-facing outputs by policy.
 */
const LANGS = [
  { code: "en", label: "EN", full: "English" },
  { code: "es", label: "ES", full: "Español (LatAm)" },
  { code: "pt", label: "PT", full: "Português (Brasil)" },
];

export default function TranslationPanel({ content, docType, testid = "translation-panel" }) {
  const [lang, setLang] = useState(() => localStorage.getItem("patient_output_lang") || "en");
  const [translated, setTranslated] = useState(null);   // {translated_text, provider, warnings, cached}
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    if (lang === "en") { setTranslated(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/translate", {
          text: content,
          target_lang: lang,
          document_type: docType || null,
          preserve_medical_terms: true,
        });
        if (!cancelled) setTranslated(data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Translation failed");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [content, lang, docType]);

  const pickLang = (code) => {
    setLang(code);
    setShowOriginal(false);
    localStorage.setItem("patient_output_lang", code);
  };

  const preservedTerms = (translated?.warnings || [])
    .filter(w => w.startsWith("medical_term_preserved:"))
    .map(w => w.split(":", 2)[1]);
  const isStub = translated?.provider === "stub";
  const displayText = (lang === "en" || showOriginal)
    ? content
    : (translated?.translated_text || content);

  return (
    <div className="mt-2" data-testid={testid}>
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-1.5 text-[11px] text-brand-muted">
          <Languages className="w-3.5 h-3.5" />
          Patient language
        </div>
        <div className="inline-flex rounded-full border border-brand overflow-hidden" data-testid={`${testid}-pill`}>
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => pickLang(l.code)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                lang === l.code
                  ? "bg-brand-primary text-white"
                  : "text-brand-muted hover:bg-brand-bg"
              }`}
              title={l.full}
              data-testid={`${testid}-pill-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        {loading && (
          <span className="inline-flex items-center gap-1 text-[11px] text-brand-muted">
            <Loader2 className="w-3 h-3 animate-spin" /> translating…
          </span>
        )}
        {translated?.cached && !loading && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`${testid}-cached`}>cached</span>
        )}
        {isStub && !loading && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 inline-flex items-center gap-1" data-testid={`${testid}-stub-badge`}>
            <ShieldAlert className="w-3 h-3" /> STUB — demo only, not real translation
          </span>
        )}
        {lang !== "en" && translated && !loading && (
          <button
            onClick={() => setShowOriginal(v => !v)}
            className="ml-auto text-[11px] text-brand-primary hover:underline inline-flex items-center gap-1"
            data-testid={`${testid}-show-original`}
          >
            <RotateCcw className="w-3 h-3" />
            {showOriginal ? "Show translated" : "Show original English"}
          </button>
        )}
      </div>

      <div className="whitespace-pre-wrap text-sm text-brand-text leading-relaxed" data-testid={`${testid}-body`}>
        {displayText}
      </div>

      {preservedTerms.length > 0 && lang !== "en" && !showOriginal && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`${testid}-preserved-chips`}>
          <span className="text-[10px] text-brand-muted mr-1">Preserved verbatim:</span>
          {preservedTerms.map((t, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700 font-mono">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
